/**
 * A small cache so pages appear instantly instead of showing a skeleton.
 *
 * Pages seed their state from here and render immediately, then refresh in the
 * background. Held in memory for speed and mirrored into sessionStorage so a
 * refresh doesn't throw it away — reloading a page you were just looking at
 * shouldn't feel slower than moving to it.
 *
 * sessionStorage rather than localStorage on purpose: it belongs to this tab
 * and is gone when the browser closes, so business figures aren't left sitting
 * on a shared computer. It's also cleared on sign-out.
 *
 * Keys are scoped by client, so switching client never shows the previous
 * client's figures.
 */
const PREFIX = "cache:";
const store = new Map();

/** Anything larger than this isn't worth persisting — it slows the write down
 *  more than the read saves. It stays in memory either way. */
const MAX_PERSISTED = 400_000;

function persist(key, value) {
  try {
    const text = JSON.stringify(value);
    if (text.length > MAX_PERSISTED) {
      sessionStorage.removeItem(PREFIX + key);
      return;
    }
    sessionStorage.setItem(PREFIX + key, text);
  } catch {
    // Private browsing, or the quota is full. The in-memory copy still works;
    // this only means a refresh will refetch.
  }
}

export function cacheKey(name, tenantId) {
  return `${tenantId || "none"}:${name}`;
}

export function cacheGet(key) {
  if (store.has(key)) return store.get(key);
  try {
    const saved = sessionStorage.getItem(PREFIX + key);
    if (saved === null) return undefined;
    const value = JSON.parse(saved);
    store.set(key, value);
    return value;
  } catch {
    return undefined;
  }
}

export function cacheSet(key, value) {
  store.set(key, value);
  persist(key, value);
  return value;
}

/**
 * Drop cached pages whose key contains a word.
 *
 * Renaming an item changes what purchases and sales show, so those pages have
 * to be refetched rather than served from a cache made before the rename.
 */
export function cacheDrop(word) {
  for (const key of [...store.keys()]) {
    if (key.includes(word)) store.delete(key);
  }
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(PREFIX) && key.includes(word)) sessionStorage.removeItem(key);
    }
  } catch {
    /* nothing persisted */
  }
}

/** Called on sign-out so the next person doesn't inherit anything. */
export function cacheClear() {
  store.clear();
  try {
    for (const key of Object.keys(sessionStorage)) {
      if (key.startsWith(PREFIX)) sessionStorage.removeItem(key);
    }
  } catch {
    /* nothing persisted */
  }
}
